import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate, useParams, Link, NavLink } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspace } from '@/hooks/useWorkspace';
import {
    LayoutDashboard, Users, TrainFront, LogOut, Network, Settings, Plug,
    Layout, GitBranch, Target, ListTodo, Route as RouteIcon,
    Map, Plus, Trash2, ChevronRight, Pencil,
    type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuItem,
    SidebarMenuButton,
    SidebarMenuSub,
    SidebarMenuSubItem,
    SidebarMenuSubButton,
    SidebarProvider,
    SidebarRail,
    SidebarTrigger,
    SidebarInset,
} from '@/components/ui/sidebar';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
    FieldGroup, BrutalInput, BrutalTextarea, SubmitButton,
    ConfirmDialog,
} from '@/components/ds';
import { WorkspaceSwitcher } from '@/components/layout/WorkspaceSwitcher';
import { roadmapsApi, storyMapsApi } from '../../apps/roadmap-toolkit/shared/api';
import { createStoryMap } from '@/lib/createStoryMap';
import type { Roadmap, UserStoryMap } from '../../apps/roadmap-toolkit/shared/types';

const VIEW_TYPES = [
    { to: 'goal-oriented', label: 'Now-Next-Later', icon: Layout },
    { to: 'metro', label: 'Metro Map', icon: RouteIcon },
    { to: 'story-map', label: 'User Story Map', icon: ListTodo },
    { to: 'bold-steps', label: 'Bold Steps', icon: Target },
    { to: 'functional', label: 'Functional', icon: GitBranch },
    { to: 'goal-driven', label: 'Goal Driven', icon: Target },
];

export default function MainLayout() {
    const { user, signOut } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const location = useLocation();
    const navigate = useNavigate();
    const { roadmapId, storyMapId } = useParams<{ roadmapId: string; storyMapId: string }>();

    // Roadmap state
    const [roadmaps, setRoadmaps] = useState<Roadmap[]>([]);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [newRoadmapName, setNewRoadmapName] = useState('');
    const [newRoadmapDesc, setNewRoadmapDesc] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [deleteId, setDeleteId] = useState<string | null>(null);

    // Story map state
    const [storyMaps, setStoryMaps] = useState<UserStoryMap[]>([]);
    const [isCreateStoryMapOpen, setIsCreateStoryMapOpen] = useState(false);
    const [newStoryMapName, setNewStoryMapName] = useState('');
    const [newStoryMapDesc, setNewStoryMapDesc] = useState('');
    const [isCreatingStoryMap, setIsCreatingStoryMap] = useState(false);
    const [deleteStoryMapId, setDeleteStoryMapId] = useState<string | null>(null);

    const loadRoadmaps = useCallback(async () => {
        const { data, error } = await roadmapsApi.list();
        if (error) console.error(error);
        else setRoadmaps((data as Roadmap[]) || []);
    }, []);

    const loadStoryMaps = useCallback(async () => {
        const { data, error } = await storyMapsApi.list();
        if (error) console.error(error);
        else setStoryMaps((data as UserStoryMap[]) || []);
    }, []);

    useEffect(() => {
        if (!user) return;
        loadRoadmaps();
        loadStoryMaps();
    }, [user, loadRoadmaps, loadStoryMaps]);

    // Listen for create events from child routes
    useEffect(() => {
        const handler = () => setIsCreateOpen(true);
        window.addEventListener('roadmap:create', handler);
        return () => window.removeEventListener('roadmap:create', handler);
    }, []);

    const handleCreate = async () => {
        if (!user || !newRoadmapName.trim()) return;
        setIsCreating(true);
        const { data, error } = await roadmapsApi.create({
            owner_id: user.id,
            name: newRoadmapName,
            description: newRoadmapDesc,
        });
        if (error) {
            toast.error('Failed to create roadmap');
            console.error(error);
        } else {
            toast.success('Roadmap created');
            setNewRoadmapName('');
            setNewRoadmapDesc('');
            setIsCreateOpen(false);
            await loadRoadmaps();
            if (data) navigate(`/roadmaps/${data.id}/goal-oriented`);
        }
        setIsCreating(false);
    };

    const handleDelete = async (id: string) => {
        const { error } = await roadmapsApi.delete(id);
        if (error) {
            toast.error('Failed to delete roadmap');
            console.error(error);
        } else {
            toast.success('Roadmap deleted');
            await loadRoadmaps();
            if (id === roadmapId) {
                navigate('/roadmaps', { replace: true });
            }
        }
        setDeleteId(null);
    };

    const handleCreateStoryMap = async () => {
        if (!user) return;
        setIsCreatingStoryMap(true);
        const ok = await createStoryMap(
            { name: newStoryMapName, description: newStoryMapDesc, userId: user.id },
            {
                create: storyMapsApi.create,
                toastError: (msg) => toast.error(msg),
                toastSuccess: (msg) => toast.success(msg),
                navigate,
                onSuccess: async () => {
                    setNewStoryMapName('');
                    setNewStoryMapDesc('');
                    setIsCreateStoryMapOpen(false);
                    await loadStoryMaps();
                },
            },
        );
        // Even on failure the dialog stays open so the user can retry — the
        // toast in toastError is the user-visible feedback (audit H6).
        void ok;
        setIsCreatingStoryMap(false);
    };

    const handleDeleteStoryMap = async (id: string) => {
        const { error } = await storyMapsApi.delete(id);
        if (error) {
            toast.error('Failed to delete story map');
            console.error(error);
        } else {
            toast.success('Story map deleted');
            await loadStoryMaps();
            if (id === storyMapId) {
                navigate('/dashboard', { replace: true });
            }
        }
        setDeleteStoryMapId(null);
    };

    // Rename state
    const [renameId, setRenameId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [renameType, setRenameType] = useState<'roadmap' | 'storymap'>('roadmap');

    const startRename = (id: string, currentName: string, type: 'roadmap' | 'storymap') => {
        setRenameId(id);
        setRenameValue(currentName);
        setRenameType(type);
    };

    const handleRename = async () => {
        if (!renameId || !renameValue.trim()) { setRenameId(null); return; }
        if (renameType === 'roadmap') {
            const { error } = await roadmapsApi.update(renameId, { name: renameValue.trim() });
            if (error) toast.error('Failed to rename roadmap');
            else { toast.success('Roadmap renamed'); await loadRoadmaps(); }
        } else {
            const { error } = await storyMapsApi.update(renameId, { name: renameValue.trim() });
            if (error) toast.error('Failed to rename story map');
            else { toast.success('Story map renamed'); await loadStoryMaps(); }
        }
        setRenameId(null);
    };

    const isRoadmaps = location.pathname.startsWith('/roadmaps');
    const isStoryMaps = location.pathname.startsWith('/story-maps');
    const isFullWidth = isRoadmaps || isStoryMaps;
    const activeView = VIEW_TYPES.find(v => location.pathname.includes(`/${v.to}`))?.to;

    return (
        <SidebarProvider>
            <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-background focus:text-foreground focus:top-0 focus:left-0 border-2 border-black">
                Skip to main content
            </a>
            <Sidebar collapsible="icon" className="border-r-2 border-black">
                <SidebarHeader className="border-b-2 border-black p-3">
                    <SidebarMenu>
                        <SidebarMenuItem>
                            <WorkspaceSwitcher />
                        </SidebarMenuItem>
                    </SidebarMenu>
                </SidebarHeader>

                <SidebarContent className="p-2">
                    <SidebarMenu className="gap-1">
                        <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" isActive={location.pathname === '/dashboard'} />
                        <NavItem to="/personas" icon={Users} label="Personas" isActive={location.pathname.startsWith('/personas')} />
                        <NavItem to="/maps" icon={Network} label="Maps" isActive={location.pathname.startsWith('/maps')} />

                        {/* Roadmaps accordion */}
                        <Collapsible defaultOpen={isRoadmaps} asChild>
                            <SidebarMenuItem>
                                <CollapsibleTrigger asChild>
                                    <SidebarMenuButton
                                        tooltip="Roadmaps"
                                        isActive={isRoadmaps}
                                    >
                                        <TrainFront className="size-4" />
                                        <span className="uppercase font-bold tracking-wide text-xs">Roadmaps</span>
                                        <ChevronRight className="ml-auto size-4 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                                    </SidebarMenuButton>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <SidebarMenuSub>
                                        {roadmaps.map((rm) => (
                                            <Collapsible key={rm.id} defaultOpen={rm.id === roadmapId} asChild>
                                                <SidebarMenuSubItem>
                                                    <div className="group/rm flex items-center">
                                                        {renameId === rm.id ? (
                                                            <input
                                                                autoFocus
                                                                className="flex-1 text-xs px-2 py-1 border border-black rounded bg-white"
                                                                value={renameValue}
                                                                onChange={(e) => setRenameValue(e.target.value)}
                                                                onBlur={handleRename}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setRenameId(null); }}
                                                            />
                                                        ) : (
                                                            <>
                                                                <CollapsibleTrigger asChild>
                                                                    <SidebarMenuSubButton
                                                                        asChild
                                                                        className={cn(
                                                                            'flex-1',
                                                                            rm.id === roadmapId && 'text-orange-500 font-bold'
                                                                        )}
                                                                    >
                                                                        <button className="flex items-center gap-1 w-full text-left">
                                                                            <Map className="size-3.5 shrink-0" />
                                                                            <span className="truncate" title={rm.name}>{rm.name}</span>
                                                                            <ChevronRight className="ml-auto size-3 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-90" />
                                                                        </button>
                                                                    </SidebarMenuSubButton>
                                                                </CollapsibleTrigger>
                                                                <button
                                                                    className="p-0.5 opacity-0 group-hover/rm:opacity-100 hover:bg-sidebar-accent rounded shrink-0"
                                                                    onClick={(e) => { e.stopPropagation(); startRename(rm.id, rm.name, 'roadmap'); }}
                                                                >
                                                                    <Pencil className="size-3" />
                                                                </button>
                                                            </>
                                                        )}
                                                        <button
                                                            className="p-0.5 opacity-0 group-hover/rm:opacity-100 hover:bg-sidebar-accent rounded shrink-0"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setDeleteId(rm.id);
                                                            }}
                                                        >
                                                            <Trash2 className="size-3 text-red-500" />
                                                        </button>
                                                    </div>
                                                    <CollapsibleContent>
                                                        <SidebarMenuSub>
                                                            {VIEW_TYPES.map((view) => {
                                                                const viewPath = `/roadmaps/${rm.id}/${view.to}`;
                                                                return (
                                                                    <SidebarMenuSubItem key={view.to}>
                                                                        <SidebarMenuSubButton
                                                                            asChild
                                                                            size="sm"
                                                                            isActive={rm.id === roadmapId && activeView === view.to}
                                                                        >
                                                                            <NavLink
                                                                                to={viewPath}
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    navigate(viewPath);
                                                                                }}
                                                                            >
                                                                                <view.icon className="size-3.5 shrink-0" />
                                                                                <span>{view.label}</span>
                                                                            </NavLink>
                                                                        </SidebarMenuSubButton>
                                                                    </SidebarMenuSubItem>
                                                                );
                                                            })}
                                                        </SidebarMenuSub>
                                                    </CollapsibleContent>
                                                </SidebarMenuSubItem>
                                            </Collapsible>
                                        ))}
                                        {/* Add new roadmap button */}
                                        <SidebarMenuSubItem>
                                            <SidebarMenuSubButton
                                                asChild
                                                size="sm"
                                            >
                                                <button
                                                    onClick={() => setIsCreateOpen(true)}
                                                    className="flex items-center gap-1 w-full text-left text-muted-foreground hover:text-foreground"
                                                >
                                                    <Plus className="size-3.5 shrink-0" />
                                                    <span>New Roadmap</span>
                                                </button>
                                            </SidebarMenuSubButton>
                                        </SidebarMenuSubItem>
                                    </SidebarMenuSub>
                                </CollapsibleContent>
                            </SidebarMenuItem>
                        </Collapsible>

                        {/* User Story Maps accordion */}
                        <Collapsible defaultOpen={isStoryMaps} asChild>
                            <SidebarMenuItem>
                                <CollapsibleTrigger asChild>
                                    <SidebarMenuButton
                                        tooltip="User Story Maps"
                                        isActive={isStoryMaps}
                                    >
                                        <ListTodo className="size-4" />
                                        <span className="uppercase font-bold tracking-wide text-xs">Story Maps</span>
                                        <ChevronRight className="ml-auto size-4 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                                    </SidebarMenuButton>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <SidebarMenuSub>
                                        {storyMaps.map((sm) => (
                                            <SidebarMenuSubItem key={sm.id}>
                                                <div className="group/sm flex items-center">
                                                    {renameId === sm.id ? (
                                                        <input
                                                            autoFocus
                                                            className="flex-1 text-xs px-2 py-1 border border-black rounded bg-white"
                                                            value={renameValue}
                                                            onChange={(e) => setRenameValue(e.target.value)}
                                                            onBlur={handleRename}
                                                            onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setRenameId(null); }}
                                                        />
                                                    ) : (
                                                        <>
                                                            <SidebarMenuSubButton
                                                                asChild
                                                                isActive={sm.id === storyMapId}
                                                                className="flex-1"
                                                            >
                                                                <NavLink to={`/story-maps/${sm.id}`}>
                                                                    <ListTodo className="size-3.5 shrink-0" />
                                                                    <span className="truncate" title={sm.name}>{sm.name}</span>
                                                                </NavLink>
                                                            </SidebarMenuSubButton>
                                                            <button
                                                                className="p-0.5 opacity-0 group-hover/sm:opacity-100 hover:bg-sidebar-accent rounded shrink-0"
                                                                onClick={(e) => { e.stopPropagation(); startRename(sm.id, sm.name, 'storymap'); }}
                                                            >
                                                                <Pencil className="size-3" />
                                                            </button>
                                                        </>
                                                    )}
                                                    <button
                                                        className="p-0.5 opacity-0 group-hover/sm:opacity-100 hover:bg-sidebar-accent rounded shrink-0"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setDeleteStoryMapId(sm.id);
                                                        }}
                                                    >
                                                        <Trash2 className="size-3 text-red-500" />
                                                    </button>
                                                </div>
                                            </SidebarMenuSubItem>
                                        ))}
                                        <SidebarMenuSubItem>
                                            <SidebarMenuSubButton asChild size="sm">
                                                <button
                                                    onClick={() => setIsCreateStoryMapOpen(true)}
                                                    className="flex items-center gap-1 w-full text-left text-muted-foreground hover:text-foreground"
                                                >
                                                    <Plus className="size-3.5 shrink-0" />
                                                    <span>New Story Map</span>
                                                </button>
                                            </SidebarMenuSubButton>
                                        </SidebarMenuSubItem>
                                    </SidebarMenuSub>
                                </CollapsibleContent>
                            </SidebarMenuItem>
                        </Collapsible>
                    </SidebarMenu>
                </SidebarContent>

                <SidebarFooter className="border-t-2 border-black p-2">
                    <SidebarMenu className="gap-1">
                        {currentWorkspace?.role === 'admin' && (
                            <NavItem to="/teams" icon={Users} label="Teams" isActive={location.pathname.startsWith('/teams')} />
                        )}
                        <NavItem to="/integrations" icon={Plug} label="Integrations" isActive={location.pathname.startsWith('/integrations')} />
                        {currentWorkspace?.role === 'admin' && (
                            <NavItem to="/settings" icon={Settings} label="Settings" isActive={location.pathname.startsWith('/settings')} />
                        )}
                        <NavItem to="/account" icon={Users} label="Account" isActive={location.pathname === '/account'} />
                        <SidebarMenuItem>
                            <SidebarMenuButton onClick={() => signOut()} tooltip="Sign Out">
                                <LogOut className="size-4" />
                                <span className="uppercase font-bold tracking-wide text-xs">Sign Out</span>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    </SidebarMenu>
                </SidebarFooter>
                <SidebarRail />
            </Sidebar>

            <SidebarInset>
                <header className="flex h-14 shrink-0 items-center gap-2 border-b-2 border-black bg-white px-4 shadow-brutal sticky top-0 z-10">
                    <div className="flex items-center gap-3 px-2">
                        <SidebarTrigger className="border-2 border-black" />
                        <div className="h-4 w-px bg-black/20 hidden md:block" />
                        <h1 className="text-sm font-black uppercase tracking-tighter">
                            Jasper's Toolkit<span className="text-orange-500">.</span>
                        </h1>
                    </div>
                </header>
                <div id="main-content" className={`flex-1 overflow-y-auto bg-zinc-100 w-full ${isFullWidth ? '' : 'p-4 md:p-8'}`} tabIndex={-1}>
                    <Outlet />
                </div>
            </SidebarInset>

            {/* Create Roadmap Dialog */}
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogContent className="border-2 border-black shadow-brutal">
                    <DialogHeader>
                        <DialogTitle className="font-black uppercase">Create New Roadmap</DialogTitle>
                        <DialogDescription>Create a collection for your roadmap tools.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={(e) => { e.preventDefault(); handleCreate(); }}>
                        <div className="space-y-4 py-4">
                            <FieldGroup label="Name" htmlFor="name" required>
                                <BrutalInput
                                    id="name"
                                    placeholder="e.g. Q1 Product Strategy"
                                    value={newRoadmapName}
                                    onChange={(e) => setNewRoadmapName(e.target.value)}
                                />
                            </FieldGroup>
                            <FieldGroup label="Description" htmlFor="desc">
                                <BrutalTextarea
                                    id="desc"
                                    placeholder="Optional description..."
                                    rows={3}
                                    value={newRoadmapDesc}
                                    onChange={(e) => setNewRoadmapDesc(e.target.value)}
                                />
                            </FieldGroup>
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                className="border-2 border-black font-bold uppercase text-xs"
                                onClick={() => setIsCreateOpen(false)}
                            >
                                Cancel
                            </Button>
                            <SubmitButton
                                disabled={!newRoadmapName.trim()}
                                loading={isCreating}
                                loadingLabel="Creating..."
                            >
                                <Plus size={16} /> Create Roadmap
                            </SubmitButton>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Roadmap Confirmation */}
            <ConfirmDialog
                open={!!deleteId}
                onOpenChange={(open) => !open && setDeleteId(null)}
                title="Delete Roadmap?"
                description="This action cannot be undone. This will permanently delete the roadmap and all its associated data."
                confirmLabel="Delete"
                variant="destructive"
                onConfirm={() => deleteId && handleDelete(deleteId)}
            />

            {/* Create Story Map Dialog */}
            <Dialog open={isCreateStoryMapOpen} onOpenChange={setIsCreateStoryMapOpen}>
                <DialogContent className="border-2 border-black shadow-brutal">
                    <DialogHeader>
                        <DialogTitle className="font-black uppercase">Create New Story Map</DialogTitle>
                        <DialogDescription>Create a user story map for your product.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={(e) => { e.preventDefault(); handleCreateStoryMap(); }}>
                        <div className="space-y-4 py-4">
                            <FieldGroup label="Name" htmlFor="sm-name" required>
                                <BrutalInput
                                    id="sm-name"
                                    placeholder="e.g. Onboarding Flow"
                                    value={newStoryMapName}
                                    onChange={(e) => setNewStoryMapName(e.target.value)}
                                />
                            </FieldGroup>
                            <FieldGroup label="Description" htmlFor="sm-desc">
                                <BrutalTextarea
                                    id="sm-desc"
                                    placeholder="Optional description..."
                                    rows={3}
                                    value={newStoryMapDesc}
                                    onChange={(e) => setNewStoryMapDesc(e.target.value)}
                                />
                            </FieldGroup>
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                className="border-2 border-black font-bold uppercase text-xs"
                                onClick={() => setIsCreateStoryMapOpen(false)}
                            >
                                Cancel
                            </Button>
                            <SubmitButton
                                disabled={!newStoryMapName.trim()}
                                loading={isCreatingStoryMap}
                                loadingLabel="Creating..."
                            >
                                <Plus size={16} /> Create Story Map
                            </SubmitButton>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Story Map Confirmation */}
            <ConfirmDialog
                open={!!deleteStoryMapId}
                onOpenChange={(open) => !open && setDeleteStoryMapId(null)}
                title="Delete Story Map?"
                description="This action cannot be undone. This will permanently delete the story map and all its activities, steps, releases, and stories."
                confirmLabel="Delete"
                variant="destructive"
                onConfirm={() => deleteStoryMapId && handleDeleteStoryMap(deleteStoryMapId)}
            />
        </SidebarProvider>
    );
}

const NavItem = ({ to, icon: Icon, label, isActive }: { to: string, icon: LucideIcon, label: string, isActive: boolean }) => (
    <SidebarMenuItem>
        <SidebarMenuButton asChild isActive={isActive} tooltip={label}>
            <Link to={to}>
                <Icon />
                <span className="uppercase font-bold tracking-wide text-xs">{label}</span>
            </Link>
        </SidebarMenuButton>
    </SidebarMenuItem>
);
